/**
 * #1330 — Facility Power family attachment scope + split grouped-view labels.
 */

import { shouldShowFacilityPowerAdditionalInformation } from './facilityPowerAdditionalInformation';
import {
  isLegacyEnabledStepsMode,
  isLegacyResolvedEnabledStepSet,
  resolveEnabledStepIds,
} from './resolveRequestFormSteps';
import {
  getRequestFormStepCatalogEntry,
  type RequestFormStepId,
} from '../config/requestFormStepRegistry';
import {
  getStageFromSection,
  resolveSectionIdForLookup,
  SECTION_IDS,
  STAGES,
  STAGE_LABELS,
  type Stage,
} from '../constants/sectionIds';
import {
  debugAttachmentGrouping,
  summarizeDisplayGroups,
  summarizeSectionAttachmentCounts,
} from '../dev/attachmentGroupingDebug';
import { debugAttachmentScope } from '../dev/attachmentScopeDebug';

import type { SavedAttachment } from '../services/attachmentService';

export const FACILITY_POWER_FAMILY_ATTACHMENT_STEP_IDS = [
  'facility-power',
  'large-ups',
  'power-distribution',
] as const satisfies readonly RequestFormStepId[];

export type FacilityPowerAttachmentStepId =
  (typeof FACILITY_POWER_FAMILY_ATTACHMENT_STEP_IDS)[number];

export const FACILITY_POWER_SPLIT_GROUP_STAGE_PREFIX = 'facility-power-family:';

const SECTION_TO_FAMILY_STEP: Partial<
  Record<string, FacilityPowerAttachmentStepId>
> = {
  [SECTION_IDS.FACILITY_POWER]: 'facility-power',
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS]: 'large-ups',
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION]: 'power-distribution',
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL]: 'large-ups',
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL]: 'power-distribution',
};

const LEGACY_SECTION_TO_FAMILY_STEP: Partial<
  Record<string, FacilityPowerAttachmentStepId>
> = {
  facility_power_readiness: 'facility-power',
};

export interface AttachmentScopeOptions {
  enabledStepIds: readonly string[];
  storedStepIds?: string[] | null;
  requestStage?: string | null;
}

export function isFacilityPowerFamilySplitScopeMode(
  storedStepIds: string[] | null | undefined,
  enabledStepIds?: readonly string[],
): boolean {
  if (!isLegacyEnabledStepsMode(storedStepIds)) {
    return true;
  }
  if (
    enabledStepIds &&
    enabledStepIds.length > 0 &&
    !isLegacyResolvedEnabledStepSet(enabledStepIds)
  ) {
    return true;
  }
  return false;
}

/**
 * Resolve persisted scope for attachment filtering (#8070).
 * When RequestScope has not registered yet (`undefined`), fall back to the request.
 * When scope is explicitly `null` (legacy), fall back to `request.enabledStepIds` so
 * Supporting Documents and Summary stay aligned with persisted Add Scope on the request.
 */
export function resolveStoredStepIdsForAttachments(options: {
  requestScopeStoredStepIds?: string[] | null | undefined;
  requestEnabledStepIds?: string[] | null | undefined;
}): string[] | null {
  if (options.requestScopeStoredStepIds != null) {
    return options.requestScopeStoredStepIds;
  }
  return options.requestEnabledStepIds ?? null;
}

/** Single source for attachment scope — always derive enabled from storedStepIds (#8070). */
export function resolveAttachmentScopeOptions(options: {
  storedStepIds?: string[] | null;
  requestStage?: string | null;
}): AttachmentScopeOptions {
  const storedStepIds = options.storedStepIds ?? null;
  return {
    storedStepIds,
    enabledStepIds: resolveEnabledStepIds(storedStepIds),
    requestStage: options.requestStage ?? '',
  };
}

export function facilityPowerSplitGroupStageKey(
  stepId: FacilityPowerAttachmentStepId,
): string {
  return `${FACILITY_POWER_SPLIT_GROUP_STAGE_PREFIX}${stepId}`;
}

export function parseFacilityPowerSplitGroupStageKey(
  stageKey: string | null | undefined,
): FacilityPowerAttachmentStepId | null {
  if (stageKey == null || typeof stageKey !== 'string') {
    return null;
  }
  if (!stageKey.startsWith(FACILITY_POWER_SPLIT_GROUP_STAGE_PREFIX)) {
    return null;
  }
  const stepId = stageKey.slice(
    FACILITY_POWER_SPLIT_GROUP_STAGE_PREFIX.length,
  ) as FacilityPowerAttachmentStepId;
  return FACILITY_POWER_FAMILY_ATTACHMENT_STEP_IDS.includes(stepId)
    ? stepId
    : null;
}

export function getFacilityPowerAttachmentGroupLabel(
  stepId: FacilityPowerAttachmentStepId,
): string {
  const entry = getRequestFormStepCatalogEntry(stepId);
  return entry?.pickerLabel ?? entry?.title ?? stepId;
}

export function getFacilityPowerAttachmentGroupNavOrder(
  stepId: FacilityPowerAttachmentStepId,
): number {
  return getRequestFormStepCatalogEntry(stepId)?.navOrder ?? 999;
}

/** Stage bucket keys that differ from Create Request nav step ids. */
const ATTACHMENT_GROUP_STAGE_TO_STEP_ID: Partial<
  Record<Stage, RequestFormStepId>
> = {
  [STAGES.SITE_DIMENSIONS]: 'site-information',
  [STAGES.RACKS]: 'rack-power',
  [STAGES.SUPPORTING_DOCUMENTS]: 'documents',
};

/**
 * Story 5.1 (Epic 5) — the facility-power family now spans three attachment
 * stages: `facility-power`, `large-ups`, `power-distribution`. Consumers that
 * previously keyed on `stage === STAGES.FACILITY_POWER` to route into the
 * family-split grouped view (nav-aligned `facility-power-family:{stepId}`
 * groups) must accept all three so the split key contract is preserved after
 * the taxonomy change.
 */
export function isFacilityPowerFamilyAttachmentStage(stage: Stage): boolean {
  return (
    stage === STAGES.FACILITY_POWER ||
    stage === STAGES.LARGE_UPS ||
    stage === STAGES.POWER_DISTRIBUTION
  );
}

function resolveAdditionalInformationFamilyStep(
  enabledStepIds: readonly string[],
  requestStage?: string | null,
): FacilityPowerAttachmentStepId | null {
  if (
    shouldShowFacilityPowerAdditionalInformation({
      step: 'facility-power',
      enabledStepIds,
      stage: requestStage,
    })
  ) {
    return 'facility-power';
  }
  if (
    shouldShowFacilityPowerAdditionalInformation({
      step: 'large-ups',
      enabledStepIds,
      stage: requestStage,
    })
  ) {
    return 'large-ups';
  }
  if (
    shouldShowFacilityPowerAdditionalInformation({
      step: 'power-distribution',
      enabledStepIds,
      stage: requestStage,
    })
  ) {
    return 'power-distribution';
  }
  return null;
}

/** Map attachment stage bucket → Create Request nav step id for scope filtering. */
export function resolveAttachmentStepIdForStage(
  stage: Stage,
): RequestFormStepId | null {
  const mapped = ATTACHMENT_GROUP_STAGE_TO_STEP_ID[stage];
  if (mapped) {
    return mapped;
  }
  if (getRequestFormStepCatalogEntry(stage)) {
    return stage as RequestFormStepId;
  }
  return null;
}

/**
 * Sort key for grouped attachment stage headers — matches left nav `navOrder` (#8070).
 * Facility-power split buckets (`facility-power-family:large-ups`) use catalog order directly.
 */
export function getGroupedAttachmentGroupNavOrder(groupKey: string): number {
  const facilityPowerStep = parseFacilityPowerSplitGroupStageKey(groupKey);
  if (facilityPowerStep) {
    return getFacilityPowerAttachmentGroupNavOrder(facilityPowerStep);
  }

  const directCatalog = getRequestFormStepCatalogEntry(groupKey);
  if (directCatalog) {
    return directCatalog.navOrder;
  }

  const mappedStepId = ATTACHMENT_GROUP_STAGE_TO_STEP_ID[groupKey as Stage];
  if (mappedStepId) {
    return getRequestFormStepCatalogEntry(mappedStepId)?.navOrder ?? 999;
  }

  const legacyStageIndex = STAGE_SORT_ORDER.indexOf(groupKey as Stage);
  if (legacyStageIndex >= 0) {
    return 100 + legacyStageIndex;
  }

  return 999;
}

export function resolveFacilityPowerStepForAttachmentSection(
  sectionId: string,
  options: Pick<AttachmentScopeOptions, 'enabledStepIds' | 'requestStage'>,
): FacilityPowerAttachmentStepId | null {
  const canonicalSectionId = resolveSectionIdForLookup(sectionId);

  if (canonicalSectionId === SECTION_IDS.FACILITY_POWER_ADDITIONAL) {
    return resolveAdditionalInformationFamilyStep(
      options.enabledStepIds,
      options.requestStage,
    );
  }

  return (
    SECTION_TO_FAMILY_STEP[canonicalSectionId] ??
    LEGACY_SECTION_TO_FAMILY_STEP[sectionId] ??
    LEGACY_SECTION_TO_FAMILY_STEP[sectionId.toLowerCase()] ??
    null
  );
}

export function isFacilityPowerAttachmentSection(sectionId: string): boolean {
  const stage = getStageFromSection(sectionId);
  return stage != null && isFacilityPowerFamilyAttachmentStage(stage);
}

export function shouldIncludeAttachmentSectionInScope(
  sectionId: string,
  options: AttachmentScopeOptions,
): boolean {
  const enabledStepIds = options.enabledStepIds ?? [];
  const lookupSectionId = resolveSectionIdForLookup(sectionId);

  if (!isFacilityPowerAttachmentSection(lookupSectionId)) {
    const stage =
      getStageFromSection(sectionId) ?? getStageFromSection(lookupSectionId);
    if (stage == null) {
      return false;
    }

    const stepId = resolveAttachmentStepIdForStage(stage);
    if (stepId == null) {
      return (
        isLegacyEnabledStepsMode(options.storedStepIds) ||
        isLegacyResolvedEnabledStepSet(enabledStepIds)
      );
    }

    return enabledStepIds.includes(stepId);
  }

  const familyStep = resolveFacilityPowerStepForAttachmentSection(
    lookupSectionId,
    {
      ...options,
      enabledStepIds,
    },
  );
  if (!familyStep) {
    return false;
  }

  if (
    !isFacilityPowerFamilySplitScopeMode(options.storedStepIds, enabledStepIds)
  ) {
    return enabledStepIds.includes('facility-power');
  }

  return enabledStepIds.includes(familyStep);
}

export function filterAttachmentsByScope(
  attachments: SavedAttachment[],
  options: AttachmentScopeOptions,
): SavedAttachment[] {
  return attachments.filter(attachment => {
    const sectionId = attachment.requestSection ?? '';
    if (!sectionId) {
      return true;
    }
    return shouldIncludeAttachmentSectionInScope(sectionId, options);
  });
}

export interface AttachmentDisplayGroup {
  groupKey: string;
  label: string;
  sortOrder: number;
  items: Array<{ sectionId: string; attachment: SavedAttachment }>;
}

function facilityPowerFamilyStepSortOrder(
  stepId: FacilityPowerAttachmentStepId,
): number {
  return getFacilityPowerAttachmentGroupNavOrder(stepId);
}

/**
 * Build display groups for Summary + Supporting Documents grouped view.
 * When #1330 split scopes are active, facility-power family attachments render
 * as separate nav-aligned groups instead of one combined stage bucket.
 */
export function buildAttachmentDisplayGroups(
  attachmentsBySection:
    | Record<string, SavedAttachment[] | SavedAttachment | null | undefined>
    | null
    | undefined,
  options: AttachmentScopeOptions,
): AttachmentDisplayGroup[] {
  debugAttachmentGrouping('buildAttachmentDisplayGroups.start', {
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    enabledStepIds: options?.enabledStepIds ?? [],
    storedStepIds: options?.storedStepIds ?? null,
  });

  const safeOptions: AttachmentScopeOptions = {
    enabledStepIds: options?.enabledStepIds ?? [],
    storedStepIds: options?.storedStepIds,
    requestStage: options?.requestStage,
  };
  const stageMap = new Map<
    string,
    { label: string; sortOrder: number; items: AttachmentDisplayGroup['items'] }
  >();
  const excludedSections: Array<{
    sectionId: string;
    attachmentCount: number;
    reason: 'empty' | 'outOfScope';
  }> = [];

  for (const [sectionId, rawAttachments] of Object.entries(
    attachmentsBySection ?? {},
  )) {
    const attachments = Array.isArray(rawAttachments)
      ? rawAttachments
      : rawAttachments != null
      ? [rawAttachments]
      : [];

    if (attachments.length === 0) {
      excludedSections.push({
        sectionId,
        attachmentCount: 0,
        reason: 'empty',
      });
      continue;
    }
    if (!shouldIncludeAttachmentSectionInScope(sectionId, safeOptions)) {
      excludedSections.push({
        sectionId,
        attachmentCount: attachments.length,
        reason: 'outOfScope',
      });
      debugAttachmentGrouping('buildAttachmentDisplayGroups.excluded', {
        sectionId,
        attachmentCount: attachments.length,
        enabledStepIds: safeOptions.enabledStepIds,
        storedStepIds: safeOptions.storedStepIds ?? null,
      });
      debugAttachmentScope('buildAttachmentDisplayGroups.excluded', {
        sectionId,
        attachmentCount: attachments.length,
        enabledStepIds: safeOptions.enabledStepIds,
        storedStepIds: safeOptions.storedStepIds ?? null,
      });
      continue;
    }

    const lookupSectionId = resolveSectionIdForLookup(sectionId);
    const stage =
      getStageFromSection(sectionId) ??
      getStageFromSection(lookupSectionId) ??
      STAGES.SUPPORTING_DOCUMENTS;

    let groupKey = stage;
    let label = STAGE_LABELS[stage] ?? stage;
    let sortOrder = STAGE_SORT_ORDER.indexOf(stage);
    if (sortOrder < 0) {
      sortOrder = STAGE_SORT_ORDER.length;
    }

    if (isFacilityPowerFamilyAttachmentStage(stage)) {
      const familyStep =
        resolveFacilityPowerStepForAttachmentSection(
          lookupSectionId,
          safeOptions,
        ) ??
        resolveFacilityPowerStepForAttachmentSection(sectionId, safeOptions) ??
        'facility-power';
      groupKey = facilityPowerSplitGroupStageKey(familyStep);
      label = getFacilityPowerAttachmentGroupLabel(familyStep);
      sortOrder =
        STAGE_SORT_ORDER.indexOf(STAGES.FACILITY_POWER) +
        facilityPowerFamilyStepSortOrder(familyStep) / 100;
    } else if (stage === STAGES.SITE_DIMENSIONS) {
      label =
        getRequestFormStepCatalogEntry('site-information')?.title ??
        'Site Information';
    } else if (stage === STAGES.COOLING_READINESS) {
      label =
        getRequestFormStepCatalogEntry('cooling-readiness')?.title ?? 'Cooling';
    }

    const bucket = stageMap.get(groupKey) ?? {
      label,
      sortOrder,
      items: [],
    };
    for (const attachment of attachments) {
      if (attachment == null) {
        continue;
      }
      bucket.items.push({ sectionId, attachment });
    }
    if (bucket.items.length > 0) {
      stageMap.set(groupKey, bucket);
    }
  }

  const result = [...stageMap.entries()]
    .filter((entry): entry is [string, NonNullable<(typeof entry)[1]>] => {
      return entry[1] != null && entry[1].items.length > 0;
    })
    .sort(([, a], [, b]) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(([groupKey, group]) => ({
      groupKey,
      label: group.label ?? groupKey,
      sortOrder: group.sortOrder,
      items: group.items,
    }));

  debugAttachmentGrouping('buildAttachmentDisplayGroups.result', {
    ...summarizeDisplayGroups(result),
    excludedSections,
  });

  return result;
}

const STAGE_SORT_ORDER: Stage[] = [
  STAGES.OPPORTUNITY,
  STAGES.SITE_DIMENSIONS,
  STAGES.FACILITY_POWER,
  // Story 5.1 — kept for consistency with STAGE_ORDER in sectionIds.ts, though
  // the family-split branch above overrides sortOrder with the fractional
  // (nav / 100) tie-breaker so all three family buckets sort together at the
  // Facility Power slot.
  STAGES.LARGE_UPS,
  STAGES.POWER_DISTRIBUTION,
  STAGES.COOLING_READINESS,
  STAGES.RACKS,
  // #997 QA-D: RACK_POWER removed to mirror STAGE_ORDER in sectionIds.ts — Rack
  // Power groups under Racks, so no rack-power stage is ever sorted here.
  STAGES.CUSTOMER_IT,
  STAGES.SMALL_UPS,
  STAGES.LIQUID_COOLING,
  STAGES.MONITORING,
  STAGES.SUPPORTING_DOCUMENTS,
  STAGES.FEEDBACK,
];
